import { memo, useCallback, useLayoutEffect, useState } from 'react'

import { PersonAddAlt1, Refresh } from '@mui/icons-material'
import { Button, Tooltip } from '@mui/material'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { AnimatedCounterButton } from '@shared/components/MaterialUIEnhancements/IconButton'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, ListOperationBox } from '@shared/layouts'
import { RowActions, UpdateFunctionProps, useColumns } from 'domains/Workspaces/Dashboard/ManageAccess'
import { InvitationActionButton } from 'domains/Workspaces/Dashboard/ManageAccess/Invitations'
import { UserDialogContent } from 'domains/Workspaces/Dashboard/ManageAccess/Users'
import { useWorkspaceMemberUpdateMutation } from 'hooks/mutations/smep'
import { workspaceSettingsPermissionsUserRoute } from 'pages/NWPlatform/Workspaces/Workspace/Settings'
import { workspaceQueryKeys } from 'services/queryKeys'
import { resolveDisplayName } from 'utils/userInfo'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const { environmentId, workspaceId } = workspaceSettingsPermissionsUserRoute.useParams()
  const navigate = useNavigate({ from: workspaceSettingsPermissionsUserRoute.fullPath })
  const { query } = workspaceSettingsPermissionsUserRoute.useSearch()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])

  const workspaceMemberUsersQuery = useInfiniteQuery({
    ...workspaceQueryKeys.memberList({ workspaceId, memberType: 'User', query }),
    refetchOnMount: 'always',
    select: (data) => ({ flat: data?.pages.flatMap((page) => page.items) }),
  })
  const userMembers = workspaceMemberUsersQuery.data?.flat

  const { mutate: updateUser, ...workspaceMemberUpdateMutation } = useWorkspaceMemberUpdateMutation()

  const handleUpdate = useCallback(
    ({ memberId, roles }: UpdateFunctionProps) => {
      updateUser({
        environmentId,
        workspaceId,
        memberId,
        smepWorkspaceRoleRamMemberEdit: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          roles,
        },
      })
    },
    [updateUser, environmentId, workspaceId],
  )

  const columns = useColumns({
    onUpdate: handleUpdate,
  })

  useLayoutEffect(() => {
    if (workspaceMemberUpdateMutation.isPending) {
      setLoadingRows([workspaceMemberUpdateMutation.variables.memberId])

      return () => setLoadingRows([])
    }
  }, [workspaceMemberUpdateMutation.isPending, workspaceMemberUpdateMutation.variables?.memberId])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.Dashboard.searchUsers')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <ListOperationBox>
          <Tooltip title={t('refresh')}>
            <AnimatedCounterButton
              action={useCallback(
                () =>
                  queryClient.invalidateQueries({
                    queryKey: workspaceQueryKeys.memberList({ workspaceId, memberType: 'User' }).queryKey,
                  }),
                [queryClient, workspaceId],
              )}
            >
              {(props) => <Refresh {...props} />}
            </AnimatedCounterButton>
          </Tooltip>

          <InvitationActionButton size="large" />

          <Button variant="contained" endIcon={<PersonAddAlt1 />} onClick={() => setDialogOpen(true)}>
            {t('domain:Workspace.Dashboard.add')}
          </Button>
        </ListOperationBox>
      </ActionStrip>

      <Table
        data={userMembers ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceMemberUsersQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: ['roles'] }), {})}
        ActionMenuProps={(row) => ({
          title: resolveDisplayName(row.original.userInfo) || '',
          entity: t('entity:org.member', 'Member', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        tableOptions={{
          getRowId: (member) => member.id,
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <UserDialogContent />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
